import {
  Alert,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { useCreateIdentityFromMerchantMutation } from "Auth/Api/Mutations/Migration/useCreateIdentityFromMerchantMutation";
import { useGetMerchantPersonCandidatesQuery } from "Auth/Api/Queries/Migration/useGetMerchantPersonCandidatesQuery";
import { CandidatesList } from "Components/Migration/MerchantCreateIdentity/CandidatesList/CandidatesList";
import { formatPersonName } from "Components/Migration/MerchantCreateIdentity/CandidatesList/utils";
import { ManualEntryForm } from "Components/Migration/MerchantCreateIdentity/ManualEntryForm/ManualEntryForm";
import type { FormData } from "Components/Migration/MerchantCreateIdentity/ManualEntryForm/schema";
import { useMigrationSessionHandoff } from "Components/Migration/useMigrationSessionHandoff";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { Resources, useResource } from "Translations/Resources";
import { useState } from "react";

const COMPONENT_RESOURCES = Resources.Auth.Migration.MerchantCreateIdentity;

type Props = {
  onBack: () => void;
};

export const MerchantCreateIdentity = ({ onBack }: Props) => {
  const [serverError, setServerError] = useState<string | null>(null);
  const [isDeclarationConfirmed, setIsDeclarationConfirmed] = useState(false);
  const [selectedCandidatePublicID, setSelectedCandidatePublicID] = useState<
    string | null
  >(null);
  const { t } = useResource();
  const handoffSession = useMigrationSessionHandoff();

  const { data, isLoading, isError } = useGetMerchantPersonCandidatesQuery();

  const {
    mutate: createIdentity,
    isPending,
    isSuccess,
  } = useCreateIdentityFromMerchantMutation({
    onSuccess: handoffSession,
    onError: () => {
      setServerError(t(COMPONENT_RESOURCES.SubmitError));
    },
  });

  // Keep the form locked between mutation success and the user-info-driven navigation away.
  const isFinalizing = isPending || isSuccess;

  if (isLoading) {
    return <CenteredCircularProgress size={24} />;
  }

  if (isError || !data) {
    return (
      <Stack gap={3}>
        <Alert severity="error">{t(COMPONENT_RESOURCES.LoadError)}</Alert>
        <PrimaryButton variant="outlined" color="secondary" onClick={onBack}>
          {t(Resources.Auth.Migration.BackButton)}
        </PrimaryButton>
      </Stack>
    );
  }

  const { relatedPartyCandidates, companyName, companyNumber } = data;

  const eligibleCandidates = relatedPartyCandidates.filter(
    (candidate) => !candidate.isAlreadyLinkedToIdentity,
  );

  // A single eligible representative (the common case) is preselected; with more of them the
  // user must actively pick one. Selection alone commits nothing — the CTA button below does.
  const selectedCandidate =
    eligibleCandidates.find(
      (candidate) =>
        candidate.relatedPartyPublicID === selectedCandidatePublicID,
    ) ?? (eligibleCandidates.length === 1 ? eligibleCandidates[0] : null);

  const hasCandidates = relatedPartyCandidates.length > 0;
  const companyDisplayName =
    companyName ?? t(COMPONENT_RESOURCES.UnknownCompany);

  const handleCreateFromSelected = () => {
    if (!selectedCandidate) {
      return;
    }

    setServerError(null);
    createIdentity({
      relatedPartyPublicID: selectedCandidate.relatedPartyPublicID,
      statutoryDeclarationConfirmed: isDeclarationConfirmed,
    });
  };

  const handleManualSubmit = ({
    firstName,
    lastName,
    birthDate,
    phone,
  }: FormData) => {
    setServerError(null);
    createIdentity({
      manualPerson: { firstName, lastName, birthDate, phone: phone || null },
      statutoryDeclarationConfirmed: isDeclarationConfirmed,
    });
  };

  return (
    <Stack gap={3}>
      <Typography variant="h6">{t(COMPONENT_RESOURCES.Title)}</Typography>
      <Typography variant="body2">
        {t(COMPONENT_RESOURCES.Description)}
      </Typography>
      <Stack gap={0.5}>
        <Typography variant="caption" color="text.secondary">
          {t(COMPONENT_RESOURCES.CompanyLabel)}
        </Typography>
        <Typography variant="body1">
          {companyDisplayName}
          {companyNumber &&
            ` ${t(COMPONENT_RESOURCES.CompanyNumber, { number: companyNumber })}`}
        </Typography>
      </Stack>
      <FormControlLabel
        control={
          <Checkbox
            checked={isDeclarationConfirmed}
            onChange={(_, isChecked) => setIsDeclarationConfirmed(isChecked)}
            disabled={isFinalizing}
          />
        }
        label={t(COMPONENT_RESOURCES.StatutoryDeclaration, {
          company: companyDisplayName,
        })}
      />
      {hasCandidates && (
        <>
          <CandidatesList
            relatedPartyCandidates={relatedPartyCandidates}
            selectedPublicID={selectedCandidate?.relatedPartyPublicID ?? null}
            isDisabled={isFinalizing}
            onSelect={setSelectedCandidatePublicID}
          />
          <PrimaryButton
            variant="contained"
            color="primary"
            onClick={handleCreateFromSelected}
            disabled={
              !isDeclarationConfirmed || !selectedCandidate || isFinalizing
            }
          >
            {selectedCandidate
              ? t(COMPONENT_RESOURCES.CreateAsButton, {
                  name: formatPersonName(
                    t,
                    selectedCandidate.firstName,
                    selectedCandidate.lastName,
                  ),
                })
              : t(COMPONENT_RESOURCES.CreateAsButtonNoSelection)}
          </PrimaryButton>
          <Divider />
        </>
      )}
      <ManualEntryForm
        hasCandidates={hasCandidates}
        isPending={isFinalizing}
        isSubmitDisabled={isFinalizing || !isDeclarationConfirmed}
        onSubmit={handleManualSubmit}
      />
      {serverError && <Alert severity="error">{serverError}</Alert>}
      <PrimaryButton
        variant="outlined"
        color="secondary"
        onClick={onBack}
        disabled={isFinalizing}
      >
        {t(Resources.Auth.Migration.BackButton)}
      </PrimaryButton>
    </Stack>
  );
};
